import * as React from "react";
import { useSearchParams } from "react-router-dom";
import { formatInTimeZone } from "date-fns-tz";
import { toast } from "sonner";
import type { ColumnDef } from "@tanstack/react-table";
import {
  Calendar,
  Copy,
  Download,
  GraduationCap,
  Loader2,
  RefreshCw,
  Search,
  UserCircle,
  X,
} from "lucide-react";

import {
  enrollmentsApi,
  DEFAULT_ENROLLMENT_ORDER_STATUS,
  ALL_ENROLLMENT_ORDER_STATUS,
  type EnrollmentListQuery,
  type EnrollmentListRow,
} from "@/lib/api/enrollments";
import { studentApi } from "@/lib/api";
import config from "@/config";
import {
  formatAdminBusinessEt,
  formatEnrollmentStartDate,
} from "@/lib/admin-datetime";
import { UserDetailsSheet } from "@/components/users/UserDetailsSheet";
import { usePermissions } from "@/hooks/usePermissions";
import { can } from "@/utils/permissions";
import { DataTable } from "@/components/data-table/data-table";
import { DataTableToolbar } from "@/components/data-table/data-table-toolbar";
import { useDataTable } from "@/hooks/use-data-table";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const ET_ZONE = "America/New_York";

function todayEtIsoDate(): string {
  return formatInTimeZone(new Date(), ET_ZONE, "yyyy-MM-dd");
}

function formatCurrency(amount: number | null | undefined): string {
  if (amount == null || Number.isNaN(amount)) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount);
}

function statusTone(
  status: string | null | undefined
): "success" | "warning" | "danger" | "info" | "muted" {
  if (!status) return "muted";
  const u = status.toUpperCase();
  if (u === "COMPLETED" || u === "PAID" || u === "PAYMENT_COMPLETED") return "success";
  if (u === "FAILED" || u === "CANCELLED" || u === "REFUNDED" || u === "DROPPED")
    return "danger";
  if (u === "PENDING" || u === "PENDING_PAYMENT" || u === "PAUSED") return "warning";
  if (u === "ENROLLED" || u === "ACTIVE") return "info";
  return "muted";
}

function programStatusLabel(status: string | null | undefined): string {
  const u = (status || "ACTIVE").toUpperCase();
  if (u === "CANCELLED") return "Dropped";
  if (u === "PAUSED") return "Paused";
  return u;
}

function StatusPill({
  tone,
  children,
}: {
  tone: ReturnType<typeof statusTone>;
  children: React.ReactNode;
}) {
  const map = {
    success:
      "border-emerald-500/30 bg-emerald-500/10 text-emerald-800 dark:text-emerald-200",
    warning:
      "border-amber-500/30 bg-amber-500/10 text-amber-900 dark:text-amber-200",
    danger: "border-destructive/30 bg-destructive/10 text-destructive",
    info: "border-sky-500/30 bg-sky-500/10 text-sky-800 dark:text-sky-200",
    muted: "border-border bg-muted text-muted-foreground",
  };
  return (
    <span
      className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ${map[tone]}`}
    >
      {children}
    </span>
  );
}

async function copyStudentContact(
  fullName: string | null | undefined,
  email: string,
  cellPhoneNumber: string | null | undefined
) {
  const text = [fullName || "—", email, cellPhoneNumber || "—"].join("\n");
  try {
    await navigator.clipboard.writeText(text);
    toast.success("Student details copied");
  } catch {
    toast.error("Could not copy student details");
  }
}

function SummaryCard({
  label,
  value,
  hint,
}: {
  label: string;
  value: number | string;
  hint?: string;
}) {
  return (
    <div className="rounded-lg border bg-card p-4 shadow-sm">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <p className="mt-1 text-2xl font-semibold tabular-nums">{value}</p>
      {hint ? <p className="mt-1 text-xs text-muted-foreground">{hint}</p> : null}
    </div>
  );
}

export default function EnrollmentsPage() {
  const { permissions, adminType } = usePermissions();
  const canImpersonate = can(permissions, "users.main.impersonate", adminType);
  const [searchParams] = useSearchParams();

  const [rows, setRows] = React.useState<EnrollmentListRow[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [exporting, setExporting] = React.useState(false);
  const [pageCount, setPageCount] = React.useState(-1);
  const [summary, setSummary] = React.useState({
    enrolledToday: 0,
    enrolledThisWeek: 0,
    totalMatching: 0,
  });

  const [search, setSearch] = React.useState(
    () => searchParams.get("search") ?? ""
  );
  const [program, setProgram] = React.useState("");
  const [enrolledFrom, setEnrolledFrom] = React.useState("");
  const [enrolledTo, setEnrolledTo] = React.useState("");
  const [paymentType, setPaymentType] = React.useState<string>("all");
  const [paymentStatus, setPaymentStatus] = React.useState<string>("all");
  const [showAllStatuses, setShowAllStatuses] = React.useState(false);
  const [activeStudentsOnly, setActiveStudentsOnly] = React.useState(true);

  const [sheetOpen, setSheetOpen] = React.useState(false);
  const [sheetUserId, setSheetUserId] = React.useState<string | null>(null);
  const [sheetFallbackName, setSheetFallbackName] = React.useState<string>();
  const [impersonateConfirmOpen, setImpersonateConfirmOpen] = React.useState(false);
  const [impersonatingUserId, setImpersonatingUserId] = React.useState<string | null>(
    null
  );

  const openImpersonateConfirm = React.useCallback(() => {
    if (!sheetUserId) return;
    if (!config.studentAppUrl) {
      toast.error("Student portal URL is not configured (VITE_STUDENT_APP_URL).");
      return;
    }
    setImpersonateConfirmOpen(true);
  }, [sheetUserId]);

  const confirmViewAsUser = React.useCallback(async () => {
    if (!sheetUserId) return;

    setImpersonatingUserId(sheetUserId);
    try {
      const res = await studentApi.impersonate(sheetUserId);
      const baseUrl = res?.data?.impersonationUrl;
      if (!baseUrl) {
        throw new Error("No impersonation URL returned");
      }
      const returnTo = `${window.location.origin}${window.location.pathname}${window.location.search}`;
      const url = new URL(baseUrl);
      url.searchParams.set("returnTo", returnTo);
      url.searchParams.set("sessionMode", "tab");
      window.open(url.toString(), "_blank", "noopener,noreferrer");
      toast.success(`Opened student portal as ${sheetFallbackName || "student"}`);
    } catch (e: unknown) {
      const message =
        e instanceof Error ? e.message : "Failed to start impersonation";
      toast.error(message);
    } finally {
      setImpersonatingUserId(null);
      setImpersonateConfirmOpen(false);
    }
  }, [sheetUserId, sheetFallbackName]);

  const buildQuery = React.useCallback(
    (page: number, perPage: number): EnrollmentListQuery => {
      const paymentTypeFilter: EnrollmentListQuery["paymentType"] =
        paymentType === "ONE_TIME" || paymentType === "SUBSCRIPTION"
          ? paymentType
          : undefined;
      const paymentStatusFilter: EnrollmentListQuery["paymentStatus"] =
        paymentStatus === "PENDING" ||
        paymentStatus === "COMPLETED" ||
        paymentStatus === "FAILED" ||
        paymentStatus === "REFUNDED"
          ? paymentStatus
          : undefined;

      return {
        page,
        perPage,
        search: search.trim() || undefined,
        program: program.trim() || undefined,
        enrolledFrom: enrolledFrom || undefined,
        enrolledTo: enrolledTo || undefined,
        orderStatus: showAllStatuses
          ? ALL_ENROLLMENT_ORDER_STATUS
          : DEFAULT_ENROLLMENT_ORDER_STATUS,
        paymentType: paymentTypeFilter,
        paymentStatus: paymentStatusFilter,
        activeStudentsOnly,
        sortBy: "enrolledAt",
        sortOrder: "desc",
      };
    },
    [
      search,
      program,
      enrolledFrom,
      enrolledTo,
      showAllStatuses,
      paymentType,
      paymentStatus,
      activeStudentsOnly,
    ]
  );

  const fetchEnrollments = React.useCallback(
    async (page: number, perPage: number) => {
      setLoading(true);
      try {
        const res = await enrollmentsApi.list(buildQuery(page, perPage));
        setRows(res.data.enrollments);
        setSummary(res.data.summary);
        const total = res.data.pagination.total;
        const pages = res.data.pagination.pages;
        setPageCount(pages > 0 ? pages : -1);
        if (total === 0) setPageCount(0);
      } catch (e) {
        toast.error(e instanceof Error ? e.message : "Failed to load enrollments");
      } finally {
        setLoading(false);
      }
    },
    [buildQuery]
  );

  const openStudent = React.useCallback((row: EnrollmentListRow) => {
    setSheetUserId(row.student._id);
    setSheetFallbackName(row.student.fullName || row.student.email);
    setSheetOpen(true);
  }, []);

  const columns = React.useMemo<ColumnDef<EnrollmentListRow>[]>(
    () => [
      {
        id: "student",
        accessorFn: (row) => row.student.fullName ?? row.student.email,
        meta: { label: "Student" },
        header: "Student",
        cell: ({ row }) => {
          const { fullName, email, cellPhoneNumber } = row.original.student;
          return (
            <div className="flex min-w-[160px] items-start gap-2">
              <div className="min-w-0 flex-1">
                <button
                  type="button"
                  onClick={() => openStudent(row.original)}
                  className="text-left text-sm font-medium text-foreground hover:underline"
                >
                  {fullName || "—"}
                </button>
                <p className="text-xs text-muted-foreground">{email}</p>
                <p className="text-xs text-muted-foreground">
                  {cellPhoneNumber || "—"}
                </p>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8 shrink-0 text-muted-foreground hover:text-foreground"
                aria-label={`Copy contact details for ${fullName || email}`}
                onClick={() =>
                  void copyStudentContact(fullName, email, cellPhoneNumber)
                }
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          );
        },
      },
      {
        id: "accountId",
        accessorFn: (row) => row.student.accountId ?? "",
        meta: { label: "Account ID" },
        header: "Account ID",
        cell: ({ row }) => (
          <span className="text-xs text-muted-foreground tabular-nums">
            {row.original.student.accountId || "—"}
          </span>
        ),
      },
      {
        id: "program",
        accessorFn: (row) => row.programName ?? row.program,
        meta: { label: "Program" },
        header: "Program",
        cell: ({ row }) => (
          <span className="text-sm font-medium">
            {row.original.programName || row.original.program || "—"}
          </span>
        ),
      },
      {
        id: "delivery",
        accessorFn: (row) => row.deliveryMode,
        meta: { label: "Delivery" },
        header: "Delivery",
        cell: ({ row }) => (
          <span className="text-sm text-muted-foreground">
            {row.original.deliveryMode || "—"}
          </span>
        ),
      },
      {
        id: "start",
        accessorFn: (row) => row.startDate ?? row.startMonth ?? "",
        meta: { label: "Start" },
        header: "Start",
        cell: ({ row }) => (
          <span className="text-sm text-muted-foreground whitespace-nowrap">
            {formatEnrollmentStartDate(
              row.original.startDate,
              row.original.startMonth,
            )}
          </span>
        ),
      },
      {
        id: "enrolledAt",
        accessorFn: (row) => row.enrolledAt,
        meta: { label: "Enrolled (EST)" },
        header: "Enrolled (EST)",
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-sm tabular-nums">
            {formatAdminBusinessEt(row.original.enrolledAt)}
          </span>
        ),
      },
      {
        id: "orderStatus",
        accessorFn: (row) => row.orderStatus,
        meta: { label: "Order status" },
        header: "Order status",
        cell: ({ row }) => (
          <StatusPill tone={statusTone(row.original.orderStatus)}>
            {row.original.orderStatus}
          </StatusPill>
        ),
      },
      {
        id: "programStatus",
        accessorFn: (row) => row.programStatus ?? "ACTIVE",
        meta: { label: "Program status" },
        header: "Program status",
        cell: ({ row }) => (
          <StatusPill tone={statusTone(row.original.programStatus ?? "ACTIVE")}>
            {programStatusLabel(row.original.programStatus)}
          </StatusPill>
        ),
      },
      {
        id: "paymentStatus",
        accessorFn: (row) => row.paymentStatus ?? "",
        meta: { label: "Payment" },
        header: "Payment",
        cell: ({ row }) => (
          <StatusPill tone={statusTone(row.original.paymentStatus)}>
            {row.original.paymentStatus || "—"}
          </StatusPill>
        ),
      },
      {
        id: "paymentType",
        accessorFn: (row) => row.paymentType ?? "",
        meta: { label: "Type" },
        header: "Type",
        cell: ({ row }) => (
          <span className="text-xs text-muted-foreground">
            {row.original.paymentType || "—"}
          </span>
        ),
      },
      {
        id: "installmentAmount",
        accessorFn: (row) => row.paymentAmount,
        meta: { label: "Installment amount" },
        header: "Installment amount",
        cell: ({ row }) => (
          <span className="text-sm tabular-nums font-medium">
            {formatCurrency(row.original.paymentAmount)}
          </span>
        ),
      },
      {
        id: "totalAmount",
        accessorFn: (row) => row.totalAmount,
        meta: { label: "Total amount" },
        header: "Total amount",
        cell: ({ row }) => (
          <span className="text-sm tabular-nums font-medium">
            {formatCurrency(row.original.totalAmount)}
          </span>
        ),
      },
      {
        id: "totalPaid",
        accessorFn: (row) => row.totalPaid,
        meta: { label: "Total paid" },
        header: "Total paid",
        cell: ({ row }) => (
          <span className="text-sm tabular-nums">
            {formatCurrency(row.original.totalPaid)}
          </span>
        ),
      },
      {
        id: "totalDue",
        accessorFn: (row) => row.totalDue,
        meta: { label: "Total due" },
        header: "Total due",
        cell: ({ row }) => (
          <span className="text-sm tabular-nums">
            {formatCurrency(row.original.totalDue)}
          </span>
        ),
      },
      {
        id: "progress",
        accessorFn: (row) => row.subscription?.completedPayments ?? 0,
        meta: { label: "Sub. progress" },
        header: "Sub. progress",
        cell: ({ row }) => {
          const sub = row.original.subscription;
          if (!sub?.numberOfPayments) return <span className="text-muted-foreground">—</span>;
          return (
            <span className="text-sm tabular-nums">
              {sub.completedPayments ?? 0}/{sub.numberOfPayments}
            </span>
          );
        },
      },
      {
        id: "actions",
        meta: { label: "Actions" },
        enableHiding: false,
        header: () => <span className="sr-only">Actions</span>,
        size: 88,
        cell: ({ row }) => (
          <Button variant="outline" size="sm" onClick={() => openStudent(row.original)}>
            <UserCircle className="mr-1.5 h-3.5 w-3.5" />
            View
          </Button>
        ),
      },
    ],
    [openStudent]
  );

  const { table } = useDataTable({
    data: rows,
    columns,
    pageCount,
    initialState: {
      pagination: { pageIndex: 0, pageSize: 25 },
    },
    getRowId: (r) => r.orderId,
  });

  const pageIndex = table.getState().pagination.pageIndex;
  const pageSize = table.getState().pagination.pageSize;

  React.useEffect(() => {
    void fetchEnrollments(pageIndex + 1, pageSize);
  }, [pageIndex, pageSize, fetchEnrollments]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await enrollmentsApi.exportCsv(
        buildQuery(1, 10_000)
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `enrollments-${todayEtIsoDate()}.csv`;
      document.body.appendChild(a);
      a.click();
      URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success("Enrollment export downloaded");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const applyEnrolledToday = () => {
    const d = todayEtIsoDate();
    setEnrolledFrom(d);
    setEnrolledTo(d);
    table.setPageIndex(0);
  };

  const clearDateFilters = () => {
    setEnrolledFrom("");
    setEnrolledTo("");
    table.setPageIndex(0);
  };

  return (
    <div className="space-y-6 p-1">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
            <GraduationCap className="h-7 w-7 text-muted-foreground" />
            Enrollments
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            One row per program order. Dates use US Eastern (EST). Enrolled time uses
            enrollment date when set, otherwise order created time.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => void fetchEnrollments(pageIndex + 1, pageSize)}
            disabled={loading}
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Button size="sm" onClick={() => void handleExport()} disabled={exporting}>
            {exporting ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Download className="mr-2 h-4 w-4" />
            )}
            Export CSV
          </Button>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <SummaryCard
          label="Enrolled today (EST)"
          value={summary.enrolledToday}
          hint="Matching filters + effective date today"
        />
        <SummaryCard
          label="Enrolled this week (EST)"
          value={summary.enrolledThisWeek}
          hint="Monday–today in US Eastern (EST)"
        />
        <SummaryCard
          label="Matching enrollments"
          value={summary.totalMatching}
          hint="With current filters"
        />
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Filters</CardTitle>
          <CardDescription>
            Default shows successful enrollments and active students only.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <div className="space-y-2 lg:col-span-2">
              <Label htmlFor="enrollment-search">Search</Label>
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  id="enrollment-search"
                  placeholder="Name, email, account ID, program…"
                  className="pl-9"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    table.setPageIndex(0);
                  }}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="enrollment-program">Program</Label>
              <Input
                id="enrollment-program"
                placeholder="Filter by program"
                value={program}
                onChange={(e) => {
                  setProgram(e.target.value);
                  table.setPageIndex(0);
                }}
              />
            </div>
            <div className="space-y-2">
              <Label>Payment type</Label>
              <Select
                value={paymentType}
                onValueChange={(v) => {
                  setPaymentType(v);
                  table.setPageIndex(0);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All types</SelectItem>
                  <SelectItem value="SUBSCRIPTION">Subscription</SelectItem>
                  <SelectItem value="ONE_TIME">One-time</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <div className="space-y-2">
              <Label htmlFor="enrolled-from">Enrolled from (EST)</Label>
              <Input
                id="enrolled-from"
                type="date"
                value={enrolledFrom}
                onChange={(e) => {
                  setEnrolledFrom(e.target.value);
                  table.setPageIndex(0);
                }}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="enrolled-to">Enrolled to (EST)</Label>
              <Input
                id="enrolled-to"
                type="date"
                value={enrolledTo}
                onChange={(e) => {
                  setEnrolledTo(e.target.value);
                  table.setPageIndex(0);
                }}
              />
            </div>
            <div className="space-y-2">
              <Label>Payment status</Label>
              <Select
                value={paymentStatus}
                onValueChange={(v) => {
                  setPaymentStatus(v);
                  table.setPageIndex(0);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="COMPLETED">Completed</SelectItem>
                  <SelectItem value="PENDING">Pending</SelectItem>
                  <SelectItem value="FAILED">Failed</SelectItem>
                  <SelectItem value="REFUNDED">Refunded</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-wrap items-end gap-2">
              <Button variant="secondary" size="sm" onClick={applyEnrolledToday}>
                <Calendar className="mr-1.5 h-3.5 w-3.5" />
                Enrolled today
              </Button>
              {(enrolledFrom || enrolledTo) && (
                <Button variant="ghost" size="sm" onClick={clearDateFilters}>
                  <X className="mr-1.5 h-3.5 w-3.5" />
                  Clear dates
                </Button>
              )}
            </div>
          </div>

          <div className="flex flex-wrap gap-6 border-t pt-4">
            <div className="flex items-center gap-2">
              <Checkbox
                id="active-students-only"
                checked={activeStudentsOnly}
                onCheckedChange={(c) => {
                  setActiveStudentsOnly(c === true);
                  table.setPageIndex(0);
                }}
              />
              <Label htmlFor="active-students-only" className="cursor-pointer text-sm">
                Active students only
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="all-statuses"
                checked={showAllStatuses}
                onCheckedChange={(c) => {
                  setShowAllStatuses(c === true);
                  table.setPageIndex(0);
                }}
              />
              <Label htmlFor="all-statuses" className="cursor-pointer text-sm">
                Include pending &amp; cancelled orders
              </Label>
            </div>
            {showAllStatuses && (
              <Badge variant="outline" className="text-xs">
                All order statuses
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>

      <DataTable
        table={table}
        isLoading={loading}
        loadingMessage={
          <span className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading enrollments…
          </span>
        }
        emptyMessage={
          <div className="py-12 text-center text-muted-foreground">
            <GraduationCap className="mx-auto mb-3 h-10 w-10 opacity-40" />
            <p className="text-sm">No enrollments match these filters.</p>
          </div>
        }
      >
        <DataTableToolbar table={table} />
      </DataTable>

      <UserDetailsSheet
        open={sheetOpen}
        userId={sheetUserId}
        fallbackName={sheetFallbackName}
        onOpenChange={setSheetOpen}
        onEdit={() => toast.message("Open Users page to edit account details")}
        onDelete={() => toast.message("Delete is available from the Users page")}
        onResendCredentials={() =>
          toast.message("Resend credentials is available from the Users page")
        }
        onImpersonate={openImpersonateConfirm}
        canImpersonate={canImpersonate}
        resendingCredentials={false}
        impersonating={impersonatingUserId === sheetUserId}
      />

      <AlertDialog
        open={impersonateConfirmOpen}
        onOpenChange={(open) => !open && setImpersonateConfirmOpen(false)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>View as user?</AlertDialogTitle>
            <AlertDialogDescription>
              This opens the student portal in a new tab signed in as{" "}
              <span className="font-medium">{sheetFallbackName || "this student"}</span>.
              Your admin session stays active in this tab. Actions are logged.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={!!impersonatingUserId}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => void confirmViewAsUser()}
              disabled={!sheetUserId || !!impersonatingUserId}
            >
              {impersonatingUserId ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Opening...
                </>
              ) : (
                "Open student portal"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
